import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { finalize, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { InputCheckboxModule } from '../input-checkbox/input-checkbox.module';
import { StatusLoadingModule } from '../status-loading/status-loading.module';
import { StatusNoDataModule } from '../status-no-data/status-no-data.module';
import { SchoolProgramService } from '../../services/school-program.service';

export interface ParticipantTableQuery {
  page_size: number;
  page: number;
  search: string;
}

@Component({
  selector: 'app-table-participant',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatPaginatorModule,
    InputCheckboxModule,
    StatusLoadingModule,
    StatusNoDataModule
  ],
  templateUrl: './table-participant.component.html',
  styleUrl: './table-participant.component.scss'
})
export class TableParticipantComponent implements OnChanges, OnDestroy {
  @Input() schoolProgramActivityId: number | null = null;
  @Input() academicYear: string | null = null;
  @Input() yearLevelId: any = null;
  @Input() schoolLocationId: number | null = null;
  @Input() canSelect = true;
  @Input() periodId: number | null = null;
  @Input() gender: string = 'all';

  @Output() checkedParticipantsChange = new EventEmitter<any>();

  participants: any[] = [];
  loading = false;
  total = 0;
  searchTerm = '';
  query: ParticipantTableQuery = {
    page_size: 10,
    page: 1,
    search: ''
  };
  isCheckAll = false;

  private readonly defaultQuery: ParticipantTableQuery = {
    page_size: 10,
    page: 1,
    search: ''
  };

  private requestSub?: Subscription;
  private selectedParticipantsMap = new Map<number, any>();

  constructor(
    private schoolProgramService: SchoolProgramService,
    private toastr: ToastrService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['schoolProgramActivityId'] ||
      changes['academicYear'] ||
      changes['yearLevelId'] ||
      changes['schoolLocationId'] ||
      changes['gender']
    ) {
      this.resetStateForFilterChange();

      if (this.canFetch()) {
        this.fetchParticipants();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.requestSub) {
      this.requestSub.unsubscribe();
    }
  }

  onSearch(): void {
    this.query = {
      ...this.query,
      search: (this.searchTerm || '').trim(),
      page: 1
    };

    if (this.canFetch()) {
      this.fetchParticipants();
    }
  }

  onToggleCheckAllAvailableStudents() {
    console.log(this.participants.length);
    if (!this.participants || this.participants.length === 0) {
      return;
    }

    this.isCheckAll = !this.isCheckAll;
    if (this.isCheckAll) {
      this.checkedParticipantsChange.emit('all');
    } else {
      this.checkedParticipantsChange.emit([]);
      this.selectedParticipantsMap.clear();
    }
  }

  onSearchInputChange(): void {
    if (this.searchTerm === '') {
      this.onSearch();
    }
  }

  onPageChanged(event: PageEvent): void {
    this.query = {
      ...this.query,
      page: event.pageIndex + 1,
      page_size: event.pageSize
    };

    if (this.canFetch()) {
      this.fetchParticipants();
    }
  }

  isChecked(studentId: number): boolean {
    return this.selectedParticipantsMap.has(studentId);
  }

  isCheckAllCurrentPage(): boolean {
    if (!this.participants || this.participants.length === 0 || this.isCheckAll) {
      return false;
    }

    return this.participants.every((participant) =>
      this.selectedParticipantsMap.has(participant.student_id)
    );
  }

  onToggleCheckAll(): void {
    const shouldSelectAll = !this.isCheckAllCurrentPage();

    this.participants.forEach((participant) => {
      const studentId = participant.student_id;
      if (shouldSelectAll) {
        this.selectedParticipantsMap.set(studentId, participant);
      } else {
        this.selectedParticipantsMap.delete(studentId);
      }
    });

    this.emitSelection();
  }

  onToggleRow(participant: any): void {
    const studentId = participant.student_id;
    if (this.selectedParticipantsMap.has(studentId)) {
      this.selectedParticipantsMap.delete(studentId);
    } else {
      this.selectedParticipantsMap.set(studentId, participant);
    }

    this.emitSelection();
  }

  private fetchParticipants(): void {
    if (this.requestSub) {
      this.requestSub.unsubscribe();
    }

    const params = {
      school_program_activity_id: this.schoolProgramActivityId,
      academic_year: this.academicYear,
      year_level_id: this.yearLevelId,
      school_location_id: this.schoolLocationId,
      period_id: this.periodId,
      page_size: this.query.page_size,
      page: this.query.page,
      search: this.query.search,
      gender: this.gender,
    };

    this.loading = true;
    this.requestSub = this.schoolProgramService.getUnregisteredStudentList(params)
      .subscribe({
        next: (response: any) => {
          if (response?.status) {
            this.participants = Array.isArray(response.data) ? response.data : [];
            this.total = response?.meta?.total ?? this.participants.length;
            this.syncSelectedParticipantData();
            this.loading = false;
          } else {
            this.participants = [];
            this.total = 0;
            this.loading = false;
          }
        },
        error: () => {
          this.participants = [];
          this.total = 0;
          this.loading = false;
          this.toastr.error('Cannot access server endpoint!', 'Connection Error');
        }
      });
  }

  private resetStateForFilterChange(): void {
    this.query = { ...this.defaultQuery };
    this.searchTerm = '';

    if (this.requestSub) {
      this.requestSub.unsubscribe();
    }

    this.participants = [];
    this.total = 0;
    this.loading = false;

    this.selectedParticipantsMap.clear();
    this.emitSelection();
  }

  private canFetch(): boolean {
    if (!this.academicYear || !this.schoolLocationId) {
      return false;
    }

    if (Array.isArray(this.yearLevelId)) {
      return this.yearLevelId.length > 0;
    }

    return !!this.yearLevelId;
  }

  private emitSelection(): void {
    const selectedParticipants = Array.from(this.selectedParticipantsMap.values());
    const selectedIds = selectedParticipants.map((participant) => participant.student_id);

    this.checkedParticipantsChange.emit({
      ids: selectedIds,
      participants: selectedParticipants
    });
  }

  private syncSelectedParticipantData(): void {
    if (!this.participants || this.participants.length === 0) {
      return;
    }

    this.participants.forEach((participant) => {
      const studentId = participant.student_id;
      if (this.selectedParticipantsMap.has(studentId)) {
        this.selectedParticipantsMap.set(studentId, participant);
      }
    });
  }

}
